import React, { createContext, useState, useContext, ReactNode, useCallback } from 'react';
import { Account, Environment, User } from '../../../types';
import { Toast, ToastType } from './types';
import { ToastContainer } from './toast';
import AprilFoolsPrank from './pranks/AprilFoolsPrank';

interface UpdateAccountParams {
  updates: Partial<Account>;
}

interface AppContextType {
  formAuthenticityToken: string;
  currentUser: User;
  account: Account;
  updateAccount: ({ updates }: UpdateAccountParams) => void;
  environment: Environment;
  showToast: (message: string, type: ToastType) => void;
  successToast: (message: string) => void;
  failureToast: (message: string) => void;
  infoToast: (message: string) => void;
  warningToast: (message: string) => void;
}

export const AppContext = createContext<AppContextType | undefined>(undefined);

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppContextProvider');
  }
  return context;
};

interface AppContextProviderProps {
  children: ReactNode;
  formAuthenticityToken: string;
  currentUser: User;
  account: Account;
  environment: Environment;
}

export const AppContextProvider: React.FC<AppContextProviderProps> = ({
  children,
  formAuthenticityToken,
  currentUser,
  account: initialAccount,
  environment
}) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  // Server-injected account is the initial value; updateAccount patches it in place
  // so context consumers reflect changes (e.g. channel toggles) without a full reload.
  const [account, setAccount] = useState<Account>(initialAccount);

  const updateAccount = useCallback(({ updates }: UpdateAccountParams) => {
    setAccount(current => ({ ...current, ...updates }));
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts(current => current.filter(toast => toast.id !== id));
  }, []);

  const showToast = useCallback((message: string, type: ToastType) => {
    const newToast: Toast = {
      id: Date.now().toString(),
      message,
      type,
      createdAt: Date.now(),
    };
    setToasts(current => [...current, newToast]);
  }, []);

  const successToast = useCallback(
    (message: string) => {
      showToast(message || 'Success!', ToastType.SUCCESS);
    },
    [showToast]
  );

  const failureToast = useCallback(
    (message: string) => {
      showToast(message || 'Something went wrong', ToastType.FAILURE);
    },
    [showToast]
  );

  const infoToast = useCallback(
    (message: string) => {
      showToast(message || '(info message missing)', ToastType.INFO);
    },
    [showToast]
  );

  const warningToast = useCallback(
    (message: string) => {
      showToast(message || 'Warning....', ToastType.WARNING);
    },
    [showToast]
  );

  const value = {
    formAuthenticityToken,
    currentUser,
    account,
    updateAccount,
    environment,
    // Toast functions
    showToast,
    successToast,
    failureToast,
    infoToast,
    warningToast
  };

  return (
    <AppContext.Provider value={value}>
      {children}
      <ToastContainer
        toasts={toasts}
        removeToast={removeToast}
      />
      <AprilFoolsPrank />
    </AppContext.Provider>
  );
};
